<template>
  <div>
    <div class="row evollu-form" v-if="showFilters">

      <div class="col-12 col-sm-4">
        <label class="form-label">
          {{ $t('evocall.requests.created_at') }}
        </label>
        <RangeDatePicker
          id="selected-time-date-picker"
          v-model="filters.dateRange"
          :placeholder="$t('evocall.requests.date_range')"
          :clearable="false"
          />
      </div>

      <div class="col-12 col-md-4" v-if="hasReadRequestsHistoryPermissions">
        <label class="form-label">
          {{ $t('evocall.requests.accepted_by') }}
        </label>
        <multiselect
          id="acceptees-filter"
          v-model="filters.acceptees"
          :options="accepteesOptions"
          track-by="id"
          :customLabel="formatedUserLabel"
          :placeholder="accepteesOptions.length ? $t('clinical.waiting_room_statistics.all_collaborators') : $t('clinical.waiting_room_statistics.no_collaborators')"
          :allow-empty="true"
          :multiple="true"
          :disabled="!accepteesOptions.length"
          :close-on-select="false"
          >

          <template slot="clear">
            <span class="multiselect__clear" v-if="filters.acceptees.length > 0" @mousedown.prevent.stop="filters.acceptees = []"></span>
          </template>
        </multiselect>
      </div>

      <div class="col-12 col-md-4" v-if="hasReadRequestsHistoryPermissions">
        <label class="form-label">
          {{ $t('evocall.requests.creator') }}
        </label>
        <multiselect
          id="creators-filter"
          v-model="filters.creators"
          :options="creatorsOptions"
          track-by="id"
          :customLabel="formatedUserLabel"
          :placeholder="creatorsOptions.length ? $t('clinical.waiting_room_statistics.all_collaborators') : $t('clinical.waiting_room_statistics.no_collaborators')"
          :allow-empty="true"
          :multiple="true"
          :disabled="!creatorsOptions.length"
          :close-on-select="false"
          >

          <template slot="clear">
            <span class="multiselect__clear" v-if="filters.creators.length > 0" @mousedown.prevent.stop="filters.creators = []"></span>
          </template>
        </multiselect>
      </div>

      <div class="col-12 col-md-4" v-if="hasReadRequestsHistoryPermissions">
        <label class="form-label">
          {{ $t('evocall.requests.consultation_type') }}
        </label>
        <multiselect
          id="consultation-type-filter"
          v-model="filters.consultationTypes"
          :options="consultationOptions"
          track-by="id"
          :custom-label="option => $t(`clinical.people.anamnesis.${option.name}`)"
          :placeholder="$t('evocall.requests.consultation_type_all')"
          :allow-empty="true"
          :multiple="true"
          :disabled="!consultationOptions.length"
          :close-on-select="false"
        >

          <template slot="clear">
            <span class="multiselect__clear" v-if="filters.consultationTypes.length > 0" @mousedown.prevent.stop="filters.consultationTypes = []"></span>
          </template>
        </multiselect>
      </div>
    </div>

  <v-server-table
    :columns="columns"
    :options="tableOptions"
    @row-click="onRowClicked"
    @filter="onFilterUpdate"
    id="requestHistoryTable"
    ref="requestHistoryTable"
    name="requestHistoryTable"
  >
    <span v-if="row.created_at" slot="created_at" slot-scope="{row}">
      {{ row.created_at | localTime('YYYY-MM-DD HH:mm') }}
    </span>

    <span v-if="row.acceptee" slot="acceptee.name" slot-scope="{row}">
      {{ row.acceptee.nickname || row.acceptee.name }}
    </span>

    <span v-if="row.waiting_time" slot="waiting_time" slot-scope="{row}">
      {{ getRelativeTimeString(row.waiting_time) }}
    </span>

    <span slot="appointment_duration" slot-scope="{row}">
      <span v-if="row.appointment_duration">
        {{ getRelativeTimeString(row.appointment_duration) }}
      </span>
      <span v-else>
        ({{ row.estimated_duration }} {{ $t('time.minutes') }})
      </span>
    </span>

    <span v-if="row.creator" slot="creator.name" slot-scope="{row}">
      {{ row.creator.nickname || row.creator.name }}
    </span>

    <span slot="obs" class="no-break observations__row" slot-scope="{row}">
      <div v-if="row.obs">
        <va-popover
          :message="row.obs"
          placement="left"
        >
          <i class="i-has-comment-gray"></i>
        </va-popover>
      </div>
    </span>

  </v-server-table>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { uniqBy } from 'lodash'
import TableQueryMixin from '@/mixins/TableQueryMixin'
import Multiselect from 'vue-multiselect'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import { ActivityService } from '@/api/AuthService'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import { TABLE_EVOCALL_HISTORY } from '@/local-storage/LocalStorageKeys'
import RelativeTimeMixin from '@/mixins/RelativeTimeMixin'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'

export default {
  props: {
    service: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    },
    showFilters: {
      type: Boolean,
      required: false,
      default: false
    },
    querySearch: {
      type: String,
      required: false,
      default: null
    },
    clinicalServiceId: {
      type: Number,
      required: true
    }
  },
  components: {
    Multiselect,
    RangeDatePicker
  },
  mixins: [
    TableQueryMixin,
    RelativeTimeMixin,
    MultiselectLabelFormatter
  ],
  data () {
    return {
      consultationOptions: [
        {
          id:1,
          name: 'impedance'
        },
        {
          id:2,
          name: 'vocal_audiometry'
        },
        {
          id:3,
          name: 'tonal_audiometry'
        }
      ],
      columns: [
        'created_at',
        'acceptee.name',
        'person.name',
        'facility.name',
        'waiting_time',
        'appointment_duration',
        'creator.name',
        'obs'
      ],
      queryUrl: `${this.$store.state.evocall_server}/requests?serviceId=${this.clinicalServiceId}`,
      tableOptions: {
        columnsDropdown: false,
        saveState: true,
        filterable: true,
        sortable: ['created_at'],
        customFilters: ['acceptees', 'creators', 'start', 'end', 'serviceId', 'consultationTypes'],
        headings: {
          'person.name': this.$t('evocall.requests.person_name'),
          'acceptee.name': this.$t('evocall.requests.accepted_by'),
          'entity.name': this.$t('evocall.requests.entity'),
          'facility.name': this.$t('evocall.requests.facility'),
          'waiting_time': this.$t('evocall.history.waiting_time'),
          'activity.name': this.$t('evocall.requests.activity'),
          'source.name': this.$t('evocall.requests.source'),
          'type.name': this.$t('evocall.requests.type'),
          'created_at': this.$t('evocall.requests.created_at'),
          'creator.name': this.$t('evocall.requests.creator'),
          'dispenser.name': this.$t('evocall.requests.creator'),
          'appointment_duration': this.$t('evocall.history.appointment_duration'),
          'updated_at': this.$t('evocall.requests.updated_at'),
          'obs': this.$t('evocall.requests.obs')
        },
        columnsClasses: {
          results: 'results',
          actions: 'actions',
        },
        orderBy: {
          column: 'created_at',
          ascending: 0
        }
      },
      filters: {
        creators: [],
        acceptees: [],
        consultationTypes: [],
        dateRange: {
          start: this.moment().subtract(1, 'months').format('YYYY-MM-DD'),
          end: this.moment().format('YYYY-MM-DD')
        }
      },
      hasLoadedLocalStorage: false,
      entityActivityUsersForPeriod: []
    }
  },
  created: function () {
    this.loadLocalStorage()
      .then(() => this.loadEntityActivityUsersForPeriod(this.filters.dateRange.start, this.filters.dateRange.end))
      .then(() => this.loadAccepteesAndCreators())
      .then(() => this.checkActiveFilters())
      .finally(() => {
        this.hasLoadedLocalStorage = true
        //this.reloadTable()
      })
  },
  methods: {
    loadAccepteesAndCreators: function () {
      return new Promise((resolve) => {
        if (this.hasReadRequestsHistoryPermissions) {
          const state = LocalStorageHelper.get(TABLE_EVOCALL_HISTORY)
          if (state !== null && state.customQueries) {
            if (state.customQueries.hasOwnProperty('acceptees') && typeof state.customQueries.acceptees === 'string') {
              try {
                const accepteeIds = state.customQueries.acceptees.split(',').map(id => parseInt(id))
                this.filters.acceptees = this.accepteesOptions.filter(acceptee => accepteeIds.includes(acceptee.id))
              } catch (errors) {
                console.log(errors)
              }
            }

            if (state.customQueries.hasOwnProperty('creators') && typeof state.customQueries.creators === 'string') {
              try {
                const creatorIds = state.customQueries.creators.split(',').map(id => parseInt(id))
                this.filters.creators = this.creatorsOptions.filter(creator => creatorIds.includes(creator.id))
              } catch (errors) {
                console.log(errors)
              }
            }
          }
        }

        resolve()
      })
    },
    loadEntityActivityUsersForPeriod: function (start, end) {
      return new Promise((resolve, reject) => {
        if (start && end) {
          if (this.hasReadRequestsHistoryPermissions) {
            ActivityService.getActivityUsersByEntity({
              accessType: this.accessTypes.READ,
              infoType: this.evocallInfoTypes.REQUESTS_HISTORY,
              serviceId: this.service.id,
              start: this.moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
              end: this.moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
              activities: `${this.activities.AUDIOLOGY},${this.activities.DISPENSER_FIELD},${this.activities.DISPENSER_PREMISES}`
            })
              .then(entityActivityRecords => {
                this.entityActivityUsersForPeriod = entityActivityRecords
                resolve()
              }).catch(errors => {
                console.log(errors)
                reject(errors)
              })
          } else {
            resolve()
          }
        } else {
          reject(new Error('Load Entity Activity Users For Period - Invalid args'))
        }
      })
    },
    loadLocalStorage: function () {
      return new Promise((resolve) => {
        const state = LocalStorageHelper.get(TABLE_EVOCALL_HISTORY)
        let filters = JSON.parse(JSON.stringify(this.filters))

        if (state !== null && state.customQueries) {
          if (state.customQueries.hasOwnProperty('users') && state.customQueries.users !== null && state.customQueries.users !== '') {
            let userIds = state.customQueries.users.split(',').map(id => parseInt(id))
            filters.users = this.users.filter(d => userIds.includes(d.id))
          }

          if (state.customQueries.start && state.customQueries.end) {
            filters.dateRange.start = state.customQueries.start
            filters.dateRange.end = state.customQueries.end
          }

          if (state.customQueries.hasOwnProperty('consultationTypes') && state.customQueries.consultationTypes !== null && state.customQueries.consultationTypes !== '') {
            let consultationTypesIds = state.customQueries.consultationTypes.split(',').map(id => parseInt(id))
            filters.consultationTypes = this.consultationOptions.filter(item => consultationTypesIds.includes(item.id))
          }

          this.filters = filters
        }
        resolve()
      })
    },
    checkActiveFilters: function () {
      return new Promise((resolve) => {
        this.$emit('filters-updated', this.activeFilters)
        if (this.activeFilters > 0) {
          this.$emit('update:showFilters', true)
        }
        resolve()
      })
    },
    getLocalStorageFilters: function () {
      return {
        users: this.filters.users.join(',')
      }
    },
    reloadTable: function () {
      const tableView = this.getTableReference()
      if (tableView) {
        tableView.refresh()
      }
    },
    getTableReference: function () {
      return this.$refs.requestHistoryTable
    },
    onRowClicked: function ({ row }) {
      if (row.ended_at !== null) {
        this.$emit('row-clicked', { request: row })
      } else {
        this.showToast(this.$t('evocall.history.request_has_not_finished'), this.$store.state.errorToastOptions)
      }
    }
  },
  computed: {
    waitingRoomModuleKey: function () {
      return `waiting-room-${this.serviceSlug}`
    },
    ...mapState('auth', [
      'activities',
      'accessTypes'
    ]),
    ...mapState({
      evocallInfoTypes: function (state) {
        return state[this.waitingRoomModuleKey].evocallInfoTypes
      }
    }),
    evocallService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.evocall_server) || null
    },
    activeFilters: function () {
      return Object.keys(this.filters).reduce((accumulator, currentValue) => accumulator + (Array.isArray(this.filters[currentValue]) ? (this.filters[currentValue].length > 0 ? 1 : 0) : typeof this.filters[currentValue] === 'object' ? Object.values(this.filters[currentValue]).some(value => value) ? 1 : 0 : this.filters[currentValue] !== '' ? 1 : 0), 0)
    },
    userPerformsActivities: function () {
      return this.users.find(user => user.id === this.$auth.user().id) !== undefined
    },
    hasFiltersToDisplay: function () {
      return !this.userPerformsActivities
    },
    readRequestsHistoryPermissions: function () {
      let permissions = []
      if (this.evocallService !== null) {
        permissions = this.evocallService.user_permissions.filter(p => p.info_type === this.evocallInfoTypes.REQUESTS_HISTORY && p.access_type >= this.accessTypes.READ)
      }
      return permissions
    },
    hasReadRequestsHistoryPermissions: function () {
      return this.readRequestsHistoryPermissions.length > 0
    },
    readableRequestHistoryEntities: function () {
      let entities = []
      if (this.readRequestsHistoryPermissions.some(p => p.entity_id === null)) {
        entities = [...this.evocallService.entities]
      } else {
        entities = this.evocallService.entities.filter(entity => this.readRequestsHistoryPermissions.some(p => p.entity_id === entity.id))
      }
      return entities
    },
    accepteesOptions: function () {
      return uniqBy(this.entityActivityUsersForPeriod
        .filter(entityActivityUser => entityActivityUser.activity.id === this.activities.AUDIOLOGY && entityActivityUser.hasOwnProperty('user') && entityActivityUser.user !== null)
        .map(entityActivityUser => entityActivityUser.user), 'id')
    },
    creatorsOptions: function () {
      const dispenserActivities = [this.activities.DISPENSER_FIELD, this.activities.DISPENSER_PREMISES]
      return uniqBy(this.entityActivityUsersForPeriod
        .filter(entityActivityUser => dispenserActivities.includes(entityActivityUser.activity.id) && entityActivityUser.hasOwnProperty('user') && entityActivityUser.user !== null)
        .map(entityActivityUser => entityActivityUser.user), 'id')
    }
  },
  watch: {
    querySearch: function (search) {
      if (search.length > 2 || search.length === 0) {
        this.getTableReference().setFilter(search)
      }
    },
    'filters.dateRange': function (dateRange) {
      if (this.hasLoadedLocalStorage) {
        this.$refs.requestHistoryTable.customQueries.start = dateRange.start || ''
        this.emitTableFilter('vue-tables.requestHistoryTable.filter::end', dateRange.end || '')
        this.loadEntityActivityUsersForPeriod(dateRange.start, dateRange.end)
      }
    },
    'filters.acceptees': function (acceptees) {
      if (this.hasLoadedLocalStorage && Array.isArray(acceptees)) {
        this.emitTableFilter('vue-tables.requestHistoryTable.filter::acceptees', acceptees.map(acceptee => acceptee.id).join(','))
      }
    },
    'filters.creators': function (creators) {
      if (this.hasLoadedLocalStorage && Array.isArray(creators)) {
        this.emitTableFilter('vue-tables.requestHistoryTable.filter::creators', creators.map(creator => creator.id).join(','))
      }
    },
    'filters.consultationTypes': function (consultationTypes) {
      if (this.hasLoadedLocalStorage && Array.isArray(consultationTypes)) {
        this.emitTableFilter('vue-tables.requestHistoryTable.filter::consultationTypes', consultationTypes.map(cons => cons.id).join(','))
      }
    },
    activeFilters: function (nrFilters) {
      this.$emit('filters-updated', nrFilters)
    }
  }
}
</script>

<style lang="scss">
  #requestHistoryTable {
    $assetsPath: "../../../assets";
    @import '@/sass/icons/icons-classes.scss';

    i {
      display: block;
      width: 1.5rem;
      height: 1.5rem;
    }

    .i-has-comment-gray {
      @extend .i-has-comment-gray;
    }
  }
</style>
